<template>
  <div class="flex flex-col gap-2 pt-3 mt-1 border-t border-border-separator">
    <div class="flex items-center gap-2">
      <span class="text-xs font-medium text-text-default">{{ $t('scheduled.briefFacts.title') }}</span>
      <span class="text-xs text-text-subtle">{{ $t('scheduled.briefFacts.subtitle') }}</span>
    </div>

    <div v-if="processing" class="px-1 py-3 text-sm text-text-subtle">{{ $t('scheduled.common.loading') }}</div>

    <div v-else-if="!rows.length" class="px-1 py-3 text-sm text-text-subtle">
      {{ $t('scheduled.briefFacts.empty') }}
    </div>

    <template v-else>
      <label
        v-for="row in rows"
        :key="row.key"
        class="flex items-start gap-2 p-2 bg-surface-1 border border-border-separator rounded-md cursor-pointer"
      >
        <input
          type="checkbox"
          class="mt-0.5 cursor-pointer"
          :name="'briefFact_' + row.key"
          :checked="isApproved(row.key)"
          @change="setApproved(row.key, $event.target.checked)"
        />
        <span class="flex-1 min-w-0">
          <span class="block text-sm text-text-default truncate">{{ row.tool || row.key }}</span>
          <span class="block text-xs text-text-subtle truncate">
            <template v-if="row.currentlyMined">
              {{ $t('scheduled.briefFacts.asked', { uses: row.uses, owners: ownersLabel(row) }) }}
            </template>
            <!-- Shown rather than hidden: the save replaces the whole set, so a row left out of the list
                 would be silently un-approved. -->
            <template v-else>{{ $t('scheduled.briefFacts.approvedNotMined') }}</template>
          </span>
        </span>
      </label>

      <div v-if="owners_truncated" class="text-xs text-text-subtle px-1">
        {{ $t('scheduled.briefFacts.ownersTruncated', { count: owners_scanned }) }}
      </div>

      <div class="flex items-center gap-2 pt-1">
        <button
          class="text-xs px-2 py-1 rounded bg-transparent outline outline-1 outline-brand-outline text-text-link"
          :disabled="processing || !changed"
          @click="save"
        >{{ $t('scheduled.briefFacts.saveApprovals') }}</button>
        <span v-if="message" class="text-xs text-text-subtle">{{ message }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';

export default {
  name: 'BriefFactApprovals',
  props: {
    dealerId: {
      type: String,
      required: true,
    },
  },
  emits: ['saved'],
  created() {
    this.load();
  },
  data() {
    return {
      rows: [],
      // The set the screen is currently proposing. Seeded from what the server says is approved, so a save
      // that changes nothing sends back exactly what it received.
      approved_keys: [],
      owners_scanned: 0,
      owners_truncated: false,
      processing: false,
      changed: false,
      message: '',
    };
  },
  computed: {
    ...mapStores(useMainStore),
  },
  methods: {
    load() {
      if (!this.dealerId) {
        this.rows = [];
        return;
      }
      const self = this;
      this.processing = true;
      this.changed = false;
      this.mainStore.GetBriefFactApprovals(this.dealerId).then((response) => {
        const view = response || {};
        self.rows = Array.isArray(view.candidates) ? view.candidates : [];
        self.approved_keys = self.rows.filter(r => r.approved).map(r => r.key);
        self.owners_scanned = view.ownersScanned || 0;
        self.owners_truncated = !!view.ownersTruncated;
        self.processing = false;
      }, () => {
        self.rows = [];
        self.approved_keys = [];
        self.processing = false;
        self.flashMessage(self.$t('scheduled.briefFacts.loadFailed'));
      });
    },
    save() {
      if (this.processing || !this.dealerId) return;
      const self = this;
      this.processing = true;
      this.mainStore.SetBriefFactApprovals({ dealerId: this.dealerId, factKeys: this.approved_keys }).then(() => {
        self.processing = false;
        self.changed = false;
        self.flashMessage(self.$t('scheduled.briefFacts.saved'));
        self.$emit('saved');
        self.load();
      }, () => {
        self.processing = false;
        self.flashMessage(self.$t('scheduled.briefFacts.saveFailed'));
      });
    },
    isApproved(key) {
      return this.approved_keys.includes(key);
    },
    setApproved(key, on) {
      const next = new Set(this.approved_keys);
      if (on) next.add(key);
      else next.delete(key);
      this.approved_keys = [...next];
      this.changed = true;
    },
    ownersLabel(row) {
      const owners = (row && row.owners) || [];
      if (!owners.length) return this.$t('scheduled.briefFacts.ownersThisStore');
      if (owners.length === 1) return this.$t('scheduled.briefFacts.onePerson');
      return this.$t('scheduled.briefFacts.peopleCount', { count: owners.length });
    },
    flashMessage(msg) {
      const self = this;
      this.message = msg;
      setTimeout(() => { if (self.message === msg) self.message = ''; }, 4000);
    },
  },
  watch: {
    dealerId() {
      this.load();
    },
  },
};
</script>
